import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ImageIcon from '@mui/icons-material/Image';
import AirIcon from '@mui/icons-material/Air';

import { Spinner } from '../..';
import messages from '../../../static/messages';
import airflowItemsServices from '../../../services/airflowItemsServices';
import resourceServices from '../../../services/resourceServices';
import { button } from '../../../static/theme-styles';
import { actions_column, table_container_base } from '../../../static/styles';

const AirflowItemsTable = () => {
  const [items, setItems] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const confirm = useConfirm();

  const loadItems = async () => {
    try {
      const response = await airflowItemsServices.getAll();
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ name: item.name, description: item.description || '' });
    } else {
      setEditingItem(null);
      setFormData({ name: '', description: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editingItem) {
        // Update
        const response = await airflowItemsServices.update(editingItem.id, {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
        });
        if (response.status === 200) {
          toast.success('Airflow item updated');
          handleCloseDialog();
          loadItems();
        }
      } else {
        // Create
        const response = await airflowItemsServices.create({
          name: formData.name.trim(),
          description: formData.description.trim() || null,
        });
        if (response.status === 201) {
          toast.success('Airflow item created');
          handleCloseDialog();
          loadItems();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || messages.errors.error_try_again);
    }
  };

  const handleImageClick = (itemId) => {
    setSelectedItemId(itemId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedItemId) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingId(selectedItemId);

    try {
      const uploadResponse = await resourceServices.upload(file, 'airflow');
      
      if (uploadResponse.status === 201 && uploadResponse.data) {
        const imageUrl = `/api/v1/resource/airflow/${uploadResponse.data.id}.${uploadResponse.data.mimeType.split('/')[1]}`;
        
        await airflowItemsServices.update(selectedItemId, { imageUrl });
        
        toast.success('Image uploaded successfully');
        loadItems();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingId(null);
      setSelectedItemId(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = (id) => {
    confirm({
      title: messages.confirm_message.remove,
      description: 'This will permanently delete the airflow item. This action cannot be undone.',
    })
      .then(async () => {
        try {
          await airflowItemsServices.remove(id);
          toast.success('Airflow item deleted');
          loadItems();
        } catch (error) {
          toast.error(error.response?.data?.message || messages.errors.error_try_again);
        }
      })
      .catch(() => {});
  };

  if (!items) return <Spinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AirIcon color="primary" />
          Airflow Items
        </Typography>
        <Button
          variant="contained"
          endIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={button('primary', 'secondary')}
        >
          Add New
        </Button>
      </Box>

      <Paper>
        <TableContainer sx={table_container_base}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="80px">Image</TableCell>
                <TableCell width="25%">Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell width="100px">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length ? (
                items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Tooltip title="Click to upload image">
                        <IconButton 
                          onClick={() => handleImageClick(item.id)}
                          disabled={uploadingId === item.id}
                          sx={{ p: 0 }}
                        >
                          {uploadingId === item.id ? (
                            <CircularProgress size={40} />
                          ) : item.imageUrl ? (
                            <Avatar 
                              src={item.imageUrl} 
                              variant="rounded"
                              sx={{ width: 50, height: 50 }}
                            />
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 50, height: 50, bgcolor: 'grey.200' }}>
                              <ImageIcon color="disabled" />
                            </Avatar>
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 400, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.description || <em style={{ color: '#999' }}>No description</em>}
                      </Typography>
                    </TableCell>
                    <TableCell sx={actions_column}>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleOpenDialog(item)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => handleDelete(item.id)}>
                          <DeleteForeverIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No airflow items yet</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Airflow Item' : 'Add New Airflow Item'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter description that will be shown in proposals..."
            helperText="This description will be used as default text when adding to proposals"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AirflowItemsTable;
